// @ts-check
/**
 * Tracked-change decision engine.
 *
 * Single atomic entry point for accept/reject of one or more logical
 * tracked changes within a story. Used by:
 *
 *   - native commands (acceptTrackedChangeById / rejectTrackedChangeById /
 *     acceptTrackedChangesBetween / rejectTrackedChangesBetween /
 *     accept|rejectAllTrackedChanges)
 *   - document-api `trackChanges.decide` for id, range, and all targets
 *   - toolbar / context-menu wrappers via the existing resolveTrackedChangeAction
 *
 * Callers MUST treat `ok: false` as an abort and MUST NOT fall through to
 * older mark-scan paths.
 *
 * The engine does NOT mutate PM state on failure. Preflight produces a
 * complete mutation plan (PM ops + comment effects + bubble lifecycle
 * payload + receipt entities) and the engine applies it under one
 * transaction once preflight succeeds.
 */

import { Slice } from 'prosemirror-model';
import { AddMarkStep, RemoveMarkStep, ReplaceStep, canJoin } from 'prosemirror-transform';

import { TrackInsertMarkName, TrackDeleteMarkName, TrackFormatMarkName } from '../constants.js';
import { CommentsPluginKey } from '../../comment/comments-plugin.js';
import { TrackChangesBasePluginKey } from '../plugins/index.js';
import { findMarkInRangeBySnapshot } from '../trackChangesHelpers/markSnapshotHelpers.js';

import { buildReviewGraph, CanonicalChangeType, SegmentSide } from './review-graph.js';
import { graphHasErrors } from './graph-invariants.js';
import {
  classifyOwnership,
  getCurrentUserIdentity,
  getChangeAuthorIdentity,
  isSameUserHighConfidence,
} from './identity.js';
import { planCommentEffects } from './comment-effects.js';

/**
 * @typedef {'accept'|'reject'} ReviewDecision
 */

/**
 * @typedef {{ kind: 'id', id: string }
 *          | { kind: 'range', from: number, to: number }
 *          | { kind: 'all' }} NormalizedDecisionTarget
 */

/**
 * @typedef {Object} DecisionDiagnostic
 * @property {string} code
 * @property {'info'|'warning'|'error'} severity
 * @property {string} message
 * @property {string[]} [changeIds]
 * @property {unknown} [details]
 */

/**
 * @typedef {Object} DecisionReceiptEntities
 * @property {string[]} createdChangeIds successor fragment ids minted by partial-range decisions.
 * @property {string[]} updatedChangeIds changes whose surviving coverage changed.
 * @property {Array<{ id: string, cause?: string }>} removedChangeIds retired logical change ids.
 * @property {Array<{ id: string, cause: string }>} deletedComments comment threads removed as side effects.
 * @property {Array<{ id: string, cause: string }>} detachedComments comment threads whose anchors survive but should detach from tracked-change threading.
 * @property {Array<{ id: string, cause: string }>} shrunkenComments comment threads that shrank.
 * @property {Array<{ changeId: string }>} affectedChildren child ids that retired with their parent.
 */

/**
 * @typedef {Object} DecisionResult
 * @property {true} ok
 * @property {import('prosemirror-state').Transaction} tr Pending transaction the caller dispatches.
 * @property {DecisionReceiptEntities} receipt
 * @property {Set<string>} touchedChangeIds Ids the bubble lifecycle should refresh.
 * @property {DecisionDiagnostic[]} diagnostics
 */

/**
 * @typedef {Object} DecisionFailure
 * @property {false} ok
 * @property {'TARGET_NOT_FOUND'|'INVALID_TARGET'|'INVALID_INPUT'|'REVISION_MISMATCH'|'PERMISSION_DENIED'|'CAPABILITY_UNAVAILABLE'|'PRECONDITION_FAILED'|'COMMENT_CASCADE_PARTIAL'|'NO_OP'} code
 * @property {string} message
 * @property {DecisionDiagnostic[]} [diagnostics]
 * @property {unknown} [details]
 */

const TRACKED_MARK_NAMES = new Set([TrackInsertMarkName, TrackDeleteMarkName, TrackFormatMarkName]);

const failure = (code, message, extra) => ({ ok: false, code, message, ...(extra || {}) });

/**
 * Plan and apply (or fail closed) a tracked-change decision.
 *
 * @param {Object} input
 * @param {import('prosemirror-state').EditorState} input.state PM editor state at decision time.
 * @param {object} input.editor v1 editor; used for permission resolver + identity context.
 * @param {ReviewDecision} input.decision
 * @param {object} input.target Raw target shape (id, range, all, legacy aliases).
 * @param {'paired'|'independent'} [input.replacements] replacements mode.
 * @returns {DecisionResult | DecisionFailure}
 */
export const decideTrackedChanges = ({ state, editor, decision, target, replacements = 'paired' }) => {
  if (decision !== 'accept' && decision !== 'reject') {
    return failure('INVALID_TARGET', `decision must be "accept" or "reject" (got "${String(decision)}").`);
  }

  const normalized = normalizeDecisionTarget(target);
  if (!normalized.ok) return normalized.failure;

  const graph = buildReviewGraph({
    state,
    replacementsMode: replacements,
  });
  if (graphHasErrors(graph)) {
    return failure('PRECONDITION_FAILED', 'tracked review graph has invariant errors before decision.', {
      diagnostics: graph.validate(),
    });
  }

  // Resolve the target into a set of selections describing which logical
  // changes get resolved and how (full vs partial coverage). The selections
  // are deterministic across undo/redo and collaboration replay because they
  // derive from logical change ids and normalized offsets, not transient PM
  // positions.
  const selectionResult = resolveTargetToSelections({ graph, normalized: normalized.value });
  if (!selectionResult.ok) return selectionResult.failure;
  const { selections } = selectionResult;
  if (!selections.length) {
    return failure('TARGET_NOT_FOUND', 'no tracked changes match the requested decision target.');
  }

  // Permission preflight — call once per logical change. Per-id / range
  // denials abort. Accept All / Reject All skip denied changes and still
  // apply the allowed subset (SD-3845 option A, same as v2). A fully denied
  // all-target remains blocked.

  const skipDenied = normalized.value.kind === 'all';
  const permissionResult = runPermissionPreflight({ editor, decision, selections, skipDenied });
  if (!permissionResult.ok) return permissionResult.failure;
  // Resolver denied every matching change. Keep Accept All / Reject All
  // blocked instead of reporting a successful no-op.
  if (!permissionResult.selections.length) {
    const deniedChangeIds = (permissionResult.deniedSelections || []).map((selection) => selection.change.id);
    return failure('PERMISSION_DENIED', `permission denied for ${decision} of every matching tracked change.`, {
      details: { deniedChangeIds },
    });
  }
  const authorizedSelections = excludeStructuralParentsOfDeniedChanges({
    selections: permissionResult.selections,
    deniedSelections: permissionResult.deniedSelections,
    decision,
  });
  // Allowed structural parents can still be blocked when deciding them would
  // also decide a denied child. Report the blocked outcome instead of a
  // successful no-op.
  if (!authorizedSelections.length) {
    const deniedChangeIds = (permissionResult.deniedSelections || []).map((selection) => selection.change.id);
    return failure('PERMISSION_DENIED', `permission denied for ${decision} of dependent tracked changes.`, {
      details: { deniedChangeIds },
    });
  }

  // Compute the PM mutation plan + comment effects.
  const planResult = buildMutationPlan({ state, graph, selections: authorizedSelections, decision });
  if (!planResult.ok) return planResult.failure;
  const { plan } = planResult;

  // Apply the plan atomically.
  const applyResult = applyPlan({ state, plan });
  if (!applyResult.ok) return applyResult.failure;

  return {
    ok: true,
    tr: applyResult.tr,
    receipt: applyResult.receipt,
    touchedChangeIds: applyResult.touchedChangeIds,
    diagnostics: plan.diagnostics,
  };
};

// ---------------------------------------------------------------------------
// Target normalization
// ---------------------------------------------------------------------------

/**
 * Normalize the raw target shape into the canonical
 * `{ kind: 'id'|'range'|'all' }` form. Accepts legacy aliases:
 *   - `{ id: string }`                        → `{ kind: 'id', id }`
 *   - `{ scope: 'all' }`                      → `{ kind: 'all' }`
 *   - `{ from, to }`                          → `{ kind: 'range', from, to }`
 *   - canonical `{ kind: 'id'|'range'|'all' }` is passed through.
 */
/**
 * Normalize a replacement-side selector to a canonical {@link SegmentSide}
 * value, or null when absent/unrecognized.
 *
 * @param {unknown} value
 * @returns {'inserted' | 'deleted' | null}
 */
const normalizeReplacementSide = (value) => {
  if (value === SegmentSide.Inserted || value === SegmentSide.Deleted) return value;
  return null;
};

const normalizeDecisionTarget = (target) => {
  if (!target || typeof target !== 'object') {
    return { ok: false, failure: failure('INVALID_TARGET', 'decision target must be an object.') };
  }
  const t = /** @type {Record<string, unknown>} */ (target);
  if (t.kind === 'id') {
    if (typeof t.id !== 'string' || !t.id) {
      return { ok: false, failure: failure('INVALID_TARGET', 'target.kind = "id" requires a non-empty id.') };
    }
    const side = normalizeReplacementSide(t.side);
    if (t.side != null && !side) {
      return {
        ok: false,
        failure: failure('INVALID_TARGET', 'target.side must be "inserted" or "deleted" when provided.'),
      };
    }
    return { ok: true, value: { kind: 'id', id: t.id, ...(side ? { side } : {}) } };
  }
  if (t.kind === 'range') {
    const from = Number(t.from);
    const to = Number(t.to);
    if (!Number.isFinite(from) || !Number.isFinite(to) || from < 0 || to < 0 || from > to) {
      return { ok: false, failure: failure('INVALID_TARGET', 'target.kind = "range" requires from <= to.') };
    }
    return { ok: true, value: { kind: 'range', from, to } };
  }
  if (t.kind === 'all') {
    return { ok: true, value: { kind: 'all' } };
  }
  // Legacy aliases.
  if (typeof t.id === 'string' && t.id) {
    return { ok: true, value: { kind: 'id', id: t.id } };
  }
  if (t.scope === 'all') {
    return { ok: true, value: { kind: 'all' } };
  }
  if (Number.isFinite(t.from) && Number.isFinite(t.to)) {
    const from = Number(t.from);
    const to = Number(t.to);
    if (from > to) {
      return { ok: false, failure: failure('INVALID_TARGET', 'range target requires from <= to.') };
    }
    return { ok: true, value: { kind: 'range', from, to } };
  }
  return { ok: false, failure: failure('INVALID_TARGET', 'decision target shape was not recognised.') };
};

// ---------------------------------------------------------------------------
// Target → selections
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} ChangeSelection
 * @property {import('./review-graph.js').LogicalTrackedChange} change
 * @property {'full'|'partial'} coverage `full` resolves whole logical change.
 * @property {Array<{ from: number, to: number }>} ranges Concrete PM ranges to resolve.
 */

/**
 * Resolve a logical change id to its change object, preferring a STRUCTURAL
 * whole-table change when the id is shared.
 *
 * Cell text typed in a tracked-inserted row inherits the row's revision id, so
 * the same id can map to BOTH the inline text change and the structural table
 * change. The structural change registers its public-id alias only when the key
 * is free (review-graph), so a plain `changes.get(id)` can return the inline
 * child. Always selecting the structural change lets the decide cascade clear
 * the contained cell text in ONE action — and this must hold for id, range, and
 * collapsed-cursor targets alike (otherwise a range/cursor decide on a tracked
 * table resolves the inline child instead of the table).
 *
 * @param {import('./review-graph.js').TrackedReviewGraph} graph
 * @param {string} id
 * @returns {import('./review-graph.js').LogicalTrackedChange | undefined}
 */
const resolveLogicalChangeById = (graph, id) => {
  const key = String(id);
  for (const candidate of graph.changes.values()) {
    if (candidate?.type === CanonicalChangeType.Structural && String(candidate.id) === key) {
      return candidate;
    }
  }
  return graph.changes.get(id);
};

const resolveTargetToSelections = ({ graph, normalized }) => {
  if (normalized.kind === 'all') {
    /** @type {ChangeSelection[]} */
    const sel = [];
    // The `changes` map can hold a logical change under more than one key
    // (structural changes register both a table-scoped internal key and a
    // public-id alias). Dedupe by object identity so `scope:'all'` never plans
    // the same change twice.
    const seen = new Set();
    for (const change of graph.changes.values()) {
      if (seen.has(change)) continue;
      seen.add(change);
      sel.push({ change, coverage: 'full', ranges: change.segments.map((s) => ({ from: s.from, to: s.to })) });
    }
    // Document-order sort to make the apply pass deterministic and to keep
    // reverse-order step application stable.
    sel.sort((a, b) => firstFrom(a) - firstFrom(b));
    return { ok: true, selections: sel };
  }
  if (normalized.kind === 'id') {
    const change = resolveLogicalChangeById(graph, normalized.id);
    if (!change)
      return { ok: false, failure: failure('TARGET_NOT_FOUND', `no tracked change with id "${normalized.id}".`) };
    return {
      ok: true,
      selections: [
        {
          change,
          coverage: 'full',
          ranges: change.segments.map((s) => ({ from: s.from, to: s.to })),
          ...(normalized.side ? { side: normalized.side } : {}),
        },
      ],
    };
  }
  // range
  const { from, to } = normalized;
  /** @type {Map<string, ChangeSelection>} */
  const byId = new Map();
  for (const segment of graph.segments) {
    const overlapFrom = Math.max(segment.from, from);
    const overlapTo = Math.min(segment.to, to);
    if (overlapFrom >= overlapTo) {
      // collapsed cursor inside a segment also counts as "select whole change"
      // per phase0-004 "Range Decisions": collapsed range inside a change
      // resolves the whole logical change.
      if (from === to && segment.from <= from && segment.to > from) {
        const change = resolveLogicalChangeById(graph, segment.changeId);
        if (!change) continue;
        const existing = byId.get(change.id);
        if (existing) {
          existing.coverage = 'full';
          existing.ranges = change.segments.map((s) => ({ from: s.from, to: s.to }));
        } else {
          byId.set(change.id, {
            change,
            coverage: 'full',
            ranges: change.segments.map((s) => ({ from: s.from, to: s.to })),
          });
        }
      }
      continue;
    }
    const change = resolveLogicalChangeById(graph, segment.changeId);
    if (!change) continue;
    const existing = byId.get(change.id);
    if (existing) {
      existing.ranges.push({ from: overlapFrom, to: overlapTo });
      // Promote to full if the union covers all segments of the change.
      if (rangesCoverChange(existing.ranges, change)) {
        existing.coverage = 'full';
        existing.ranges = change.segments.map((s) => ({ from: s.from, to: s.to }));
      } else {
        existing.coverage = 'partial';
      }
      continue;
    }
    const isFull =
      segment.from >= from && segment.to <= to && change.segments.every((s) => s.from >= from && s.to <= to);
    byId.set(change.id, {
      change,
      coverage: isFull ? 'full' : 'partial',
      ranges: [{ from: overlapFrom, to: overlapTo }],
    });
  }
  // Sort selections by first PM position so apply order is deterministic.
  const sel = Array.from(byId.values()).sort((a, b) => firstFrom(a) - firstFrom(b));
  return { ok: true, selections: sel };
};

const firstFrom = (selection) => selection.ranges[0]?.from ?? 0;

const rangesCoverChange = (ranges, change) => {
  const sorted = [...ranges].sort((a, b) => a.from - b.from);
  // Merge into max envelope ranges
  /** @type {Array<{from:number,to:number}>} */
  const merged = [];
  for (const r of sorted) {
    const last = merged[merged.length - 1];
    if (last && r.from <= last.to) {
      last.to = Math.max(last.to, r.to);
    } else {
      merged.push({ from: r.from, to: r.to });
    }
  }
  return change.segments.every((seg) => merged.some((r) => r.from <= seg.from && r.to >= seg.to));
};

// ---------------------------------------------------------------------------
// Permission preflight
// ---------------------------------------------------------------------------

const runPermissionPreflight = ({ editor, decision, selections, skipDenied = false }) => {
  const resolver = editor?.options?.permissionResolver;
  if (typeof resolver !== 'function') return { ok: true, selections, deniedSelections: [] };

  const role = editor.options?.role ?? 'editor';
  const isInternal = Boolean(editor.options?.isInternal);
  const currentIdentity = getCurrentUserIdentity(editor);
  const authorizedSelections = [];
  const deniedSelections = [];

  for (const selection of selections) {
    const change = selection.change;
    const classification = classifyOwnership({
      currentUser: currentIdentity,
      change: getChangeAuthorIdentity({
        author: change.author,
        authorId: change.authorId,
        authorEmail: change.authorEmail,
        importedAuthor: change.importedAuthor,
      }),
    });
    const isOwn = isSameUserHighConfidence(classification);
    const permission =
      decision === 'accept' ? (isOwn ? 'RESOLVE_OWN' : 'RESOLVE_OTHER') : isOwn ? 'REJECT_OWN' : 'REJECT_OTHER';

    const allowed = resolver({
      permission,
      role,
      isInternal,
      trackedChange: {
        id: change.id,
        type: change.type,
        attrs: { author: change.author, authorId: change.authorId, authorEmail: change.authorEmail, date: change.date },
        from: selection.ranges[0]?.from ?? 0,
        to: selection.ranges[selection.ranges.length - 1]?.to ?? 0,
        segments: change.segments.map((s) => ({ from: s.from, to: s.to })),
        commentId: change.id,
      },
      comment: null,
    });
    if (allowed === false) {
      if (skipDenied) {
        deniedSelections.push(selection);
        continue;
      }
      return {
        ok: false,
        failure: failure('PERMISSION_DENIED', `permission denied for ${decision} of change "${change.id}".`, {
          details: { changeId: change.id, permission },
        }),
      };
    }
    authorizedSelections.push(selection);
  }
  return { ok: true, selections: authorizedSelections, deniedSelections };
};

const excludeStructuralParentsOfDeniedChanges = ({ selections, deniedSelections, decision }) => {
  if (!deniedSelections?.length) return selections;

  return selections.filter(({ change }) => {
    if (change.type !== CanonicalChangeType.Structural) return true;
    const structural = change.structural;
    if (!structural?.wholeTable || structural.decidable === false) return true;
    const removesTable =
      (structural.side === 'insertion' && decision === 'reject') ||
      (structural.side === 'deletion' && decision === 'accept');

    return !deniedSelections.some(({ change: deniedChange }) => {
      if (deniedChange.type === CanonicalChangeType.Structural && !removesTable) return false;
      if (!deniedChange.segments.length) return false;
      return deniedChange.segments.every(
        (segment) => structural.tableFrom <= segment.from && structural.tableTo >= segment.to,
      );
    });
  });
};

// ---------------------------------------------------------------------------
// Mutation plan
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} MutationOp
 * @property {'removeContent'|'removeMark'|'addMark'|'unwrapInsert'|'restoreFormat'|'removeFormat'|'rejectParagraphSplit'|'clearRowTrackChange'} kind
 * @property {number} from
 * @property {number} to
 * @property {string} [changeId]
 * @property {string} [side]
 * @property {import('prosemirror-model').Mark} [mark]
 * @property {Array<unknown>} [beforeMarks]
 * @property {Array<unknown>} [afterMarks]
 * @property {'inserted'|'source'} [anchor]
 */

/**
 * @typedef {Object} MutationPlan
 * @property {MutationOp[]} ops Document-order op list.
 * @property {import('./comment-effects.js').CommentEffectsPlan & { _affectedChildren?: Array<{ changeId: string }> }} commentEffects
 * @property {Set<string>} touchedChangeIds Logical ids retired/updated by the decision.
 * @property {Set<string>} retiredChangeIds Logical ids retired by the decision (subset).
 * @property {DecisionDiagnostic[]} diagnostics
 */

const buildMutationPlan = ({ state, graph, selections, decision }) => {
  /** @type {MutationOp[]} */
  const ops = [];
  /** @type {Array<{ from: number, to: number, cause: string }>} */
  const removedRanges = [];
  /** @type {Array<{ from: number, to: number, cause: string }>} */
  const resolvedRanges = [];
  /** @type {Set<string>} */
  const touched = new Set();
  /** @type {Set<string>} */
  const retired = new Set();
  /** @type {DecisionDiagnostic[]} */
  const diagnostics = [];

  // Pre-compute the table ranges that a selected whole-table structural change
  // will REMOVE (reject-insert / accept-delete). Inline tracked changes wholly
  // inside such a table must be retired/suppressed as side effects BEFORE
  // mutation planning so they do not generate their own overlapping ops or
  // cause position drift (scope:'all' and explicit multi-target decides). This
  // mirrors the `affectedChildren` retirement for removed ranges below.
  /** @type {Array<{ from: number, to: number, structuralId: string }>} */
  const structuralTableRemovals = [];
  for (const selection of selections) {
    const change = selection.change;
    if (change.type !== CanonicalChangeType.Structural) continue;
    const structural = change.structural;
    if (!structural || structural.wholeTable !== true || structural.decidable === false) continue;
    const removesTable =
      (structural.side === 'insertion' && decision === 'reject') ||
      (structural.side === 'deletion' && decision === 'accept');
    if (!removesTable) continue;
    structuralTableRemovals.push({
      from: structural.tableFrom,
      to: structural.tableTo,
      structuralId: change.id,
    });
  }
  /** @type {Set<string>} Inline ids suppressed because they live inside a removed table. */
  const suppressedInsideTable = new Set();
  const isInsideRemovedTable = (change) =>
    structuralTableRemovals.length > 0 &&
    change.type !== CanonicalChangeType.Structural &&
    change.segments.length > 0 &&
    change.segments.every((seg) =>
      structuralTableRemovals.some((range) => range.from <= seg.from && range.to >= seg.to),
    );

  // Pre-compute the table ranges that a selected whole-table structural change
  // KEEPS (accept-insert / reject-delete → the table stays as normal content).
  // Word / Google Docs treat an inserted/deleted table as ONE change: approving
  // it approves the text inside. We therefore cascade the SAME decision onto
  // every inline tracked change whose segments are wholly inside [tableFrom,
  // tableTo] — accepting an inserted table accepts the contained insertions
  // (their trackInsert marks are removed, text stays); rejecting a deleted table
  // rejects the contained changes (e.g. contained deletions are rejected so
  // their content stays). The inline change is the CHILD of the structural
  // parent here, decided together so the bubble pipeline retires it as a child.
  /** @type {Array<{ from: number, to: number, structuralId: string }>} */
  const structuralTableStays = [];
  for (const selection of selections) {
    const change = selection.change;
    if (change.type !== CanonicalChangeType.Structural) continue;
    const structural = change.structural;
    if (!structural || structural.wholeTable !== true || structural.decidable === false) continue;
    const tableStays =
      (structural.side === 'insertion' && decision === 'accept') ||
      (structural.side === 'deletion' && decision === 'reject');
    if (!tableStays) continue;
    structuralTableStays.push({
      from: structural.tableFrom,
      to: structural.tableTo,
      structuralId: change.id,
    });
  }
  const isInsideStayingTable = (change) =>
    structuralTableStays.length > 0 &&
    change.type !== CanonicalChangeType.Structural &&
    change.segments.length > 0 &&
    change.segments.every((seg) => structuralTableStays.some((range) => range.from <= seg.from && range.to >= seg.to));

  /** @type {Set<string>} Inline ids resolved as children of a staying table. */
  const cascadedInsideStayingTable = new Set();
  // Plan a single inline tracked change as a CHILD of a staying-table structural
  // decision, reusing the existing inline plan functions with FULL coverage and
  // the SAME decision. Marks it touched + retired and records it as an affected
  // child. Returns a failure to bubble up, or null on success.
  const planContainedInlineChild = (change) => {
    if (cascadedInsideStayingTable.has(change.id)) return null;
    cascadedInsideStayingTable.add(change.id);
    touched.add(change.id);
    const fullSelection = {
      change,
      coverage: 'full',
      ranges: change.segments.map((s) => ({ from: s.from, to: s.to })),
    };
    if (change.type === CanonicalChangeType.Insertion) {
      planInsertionDecision({ ops, change, selection: fullSelection, decision, removedRanges, retired });
      return null;
    }
    if (change.type === CanonicalChangeType.Deletion) {
      planDeletionDecision({ ops, change, selection: fullSelection, decision, removedRanges, retired });
      return null;
    }
    if (change.type === CanonicalChangeType.Replacement) {
      const repResult = planReplacementDecision({ ops, graph, change, decision, removedRanges, retired });
      if (!repResult.ok) return repResult.failure;
      return null;
    }
    if (change.type === CanonicalChangeType.Formatting) {
      planFormattingDecision({ ops, change, decision, retired });
      return null;
    }
    // Nested structural (a tracked table inside a tracked table cell) is out of
    // scope for the cascade; leave it for its own decide.
    cascadedInsideStayingTable.delete(change.id);
    touched.delete(change.id);
    return null;
  };

  for (const selection of selections) {
    const { change } = selection;
    // Inline tracked change fully contained in a table the decision removes:
    // suppress its own ops and retire it as a side effect. The whole-table
    // removeContent op already deletes its content; planning it independently
    // would double-plan overlapping ops / drift positions.
    if (isInsideRemovedTable(change)) {
      retired.add(change.id);
      touched.add(change.id);
      suppressedInsideTable.add(change.id);
      continue;
    }
    // Inline tracked change fully contained in a table the decision KEEPS:
    // cascade the SAME decision onto it as a child (accept-insert → its
    // trackInsert marks are removed; reject-delete → it is rejected). Routing it
    // through the dedicated child planner here (instead of the normal path
    // below) keeps `scope:'all'` from double-planning the same change. Recorded
    // as an affected child in the side-effect pass below.
    // A pPr change is EXCLUDED: it has no inline mark (its type is Formatting but
    // there is nothing to toggle), so the mark-based child planner would no-op
    // on accept / throw on reject. It falls through to the normal path below and
    // is resolved by id via planPprDecision — same guard as the side-effect
    // sweep.
    if (!change.pprChange && isInsideStayingTable(change)) {
      const failureResult = planContainedInlineChild(change);
      if (failureResult) return { ok: false, failure: failureResult };
      continue;
    }
    // Side-targeted replacement decision: resolve only the inserted OR deleted
    // half of a replacement, leaving the other half as a standalone pending
    // change. The shared change id is intentionally NOT retired — on the next
    // graph rebuild the surviving side's marks project as a standalone
    // insertion/deletion.
    if (change.type === CanonicalChangeType.Replacement && selection.side) {
      touched.add(change.id);
      const sideResult = planReplacementSideDecision({
        ops,
        change,
        decision,
        side: selection.side,
        removedRanges,
        retired,
        resolvedRanges,
      });
      if (!sideResult.ok) return { ok: false, failure: sideResult.failure };
      continue;
    }
    // A `side` selector only means something on a paired replacement (choose the
    // inserted or deleted half). Reaching here with a side set means the change
    // is NOT a paired replacement — most commonly the targeted half was already
    // resolved and only the other side survives as a standalone change. Fail
    // closed unless the surviving standalone side is exactly the one requested;
    // otherwise the generic planner below would silently resolve the OTHER side,
    // acting against the caller's intent. Only `id` targets set selection.side
    // (range/all never do), so this cannot narrow a range decision.
    if (selection.side) {
      const standaloneSide =
        change.type === CanonicalChangeType.Insertion
          ? 'inserted'
          : change.type === CanonicalChangeType.Deletion
            ? 'deleted'
            : null;
      if (standaloneSide !== selection.side) {
        return {
          ok: false,
          failure: failure(
            'INVALID_TARGET',
            `target.side "${selection.side}" does not apply: this change is not a paired replacement` +
              `${standaloneSide ? ` (its only side is "${standaloneSide}")` : ''}. ` +
              'The targeted side may have already been resolved.',
            { details: { changeId: change.id, requestedSide: selection.side, currentSide: standaloneSide } },
          ),
        };
      }
      // standaloneSide === selection.side → fall through to the standalone
      // insertion/deletion planner below, which resolves it correctly.
    }
    const isFull = selection.coverage === 'full';
    if (!isFull) {
      if (change.type === CanonicalChangeType.Structural) {
        // Whole-object atomicity (spec §8/§9/§19/TC-OPS-003): a partial-range
        // target on a structural revision that is not safely divisible MUST
        // fail closed with INVALID_INPUT and leave the document unmutated.
        return {
          ok: false,
          failure: failure(
            'INVALID_INPUT',
            'partial-range decisions are not valid on an indivisible structural revision.',
            { details: { changeId: change.id, subtype: change.subtype } },
          ),
        };
      }
      if (change.type === CanonicalChangeType.Replacement) {
        return {
          ok: false,
          failure: failure(
            'CAPABILITY_UNAVAILABLE',
            'partial-range replacement decisions are not yet fixture-backed.',
            {
              details: { changeId: change.id },
            },
          ),
        };
      }
      if (change.type === CanonicalChangeType.Formatting) {
        return {
          ok: false,
          failure: failure('CAPABILITY_UNAVAILABLE', 'partial-range formatting decisions are not yet fixture-backed.', {
            details: { changeId: change.id },
          }),
        };
      }
    }
    touched.add(change.id);
    // A pPr change flips a node attr and changes no text content, so its
    // whole-block segment must NOT enter resolvedRanges — otherwise
    // planCommentEffects would detach unrelated comments anchored in that block.
    if (isFull && !change.pprChange && !change.paragraphMarkChange) {
      for (const segment of change.segments) {
        resolvedRanges.push({
          from: segment.from,
          to: segment.to,
          cause: `${decision}:${change.id}`,
        });
      }
    }

    if (change.pprChange) {
      // Paragraph-property change (w:pPrChange). Routed before the type-based
      // branches: it is typed Formatting but has no mark, so the mark-based
      // formatting planner cannot resolve it.
      const pprResult = planPprDecision({ ops, change, decision, retired });
      if (!pprResult.ok) return { ok: false, failure: pprResult.failure };
    } else if (change.paragraphMarkChange) {
      // A paragraph mark deleted on an EMPTY block. Typed Deletion but with no
      // inline mark and no content to remove, so the mark-based deletion
      // planner has nothing to walk — only the mark half has to be resolved.
      const markResult = planParagraphMarkDecision({ ops, change, decision, retired });
      if (!markResult.ok) return { ok: false, failure: markResult.failure };
    } else if (change.type === CanonicalChangeType.Structural) {
      const structuralResult = planStructuralDecision({ ops, change, decision, removedRanges, retired });
      if (!structuralResult.ok) return { ok: false, failure: structuralResult.failure };
    } else if (
      !isFull &&
      (change.type === CanonicalChangeType.Insertion || change.type === CanonicalChangeType.Deletion)
    ) {
      const partialResult = planPartialTextDecision({
        ops,
        change,
        selection,
        decision,
        removedRanges,
        retired,
      });
      if (!partialResult.ok) return { ok: false, failure: partialResult.failure };
      for (const id of partialResult.createdChangeIds) touched.add(id);
    } else if (change.type === CanonicalChangeType.Insertion) {
      planInsertionDecision({ ops, change, selection, decision, removedRanges, retired });
    } else if (change.type === CanonicalChangeType.Deletion) {
      planDeletionDecision({ ops, change, selection, decision, removedRanges, retired });
    } else if (change.type === CanonicalChangeType.Replacement) {
      const repResult = planReplacementDecision({ ops, graph, change, decision, removedRanges, retired });
      if (!repResult.ok) return { ok: false, failure: repResult.failure };
    } else if (change.type === CanonicalChangeType.Formatting) {
      planFormattingDecision({ ops, change, decision, retired });
    } else {
      return {
        ok: false,
        failure: failure(
          'CAPABILITY_UNAVAILABLE',
          `unsupported change type "${change.type}" for change "${change.id}".`,
        ),
      };
    }
  }

  // Cascade onto contained inline children of a STAYING table that were NOT in
  // the selection set (e.g. a `{kind:'id'}` decide that targets only the
  // structural change). The whole-table change is the parent; every inline
  // tracked change wholly inside [tableFrom, tableTo] is decided with the SAME
  // decision so the table ends up clean (accept-insert) / restored with content
  // (reject-delete) and zero inline marks remain. Scope:'all' already cascaded
  // these in the main loop; `cascadedInsideStayingTable` dedups so they are not
  // planned twice. Done after the main loop so positions are consistent.
  if (structuralTableStays.length > 0) {
    // Skip by OBJECT identity, not by `change.id`. Cell text typed in a
    // tracked-inserted row inherits the row's revision id, so the inline text
    // change and the structural table change SHARE an id. An id-based skip
    // (`touched.has(change.id)`) would treat the just-decided table as if it
    // also covered the inline child and wrongly exclude it from the cascade —
    // leaving the cell text tracked after the table is accepted. The decided
    // (selected) changes and already-cascaded children are distinct objects.
    const decidedObjects = new Set(selections.map((selection) => selection.change));
    const seenStaying = new Set();
    for (const change of graph.changes.values()) {
      if (seenStaying.has(change)) continue;
      seenStaying.add(change);
      if (decidedObjects.has(change) || cascadedInsideStayingTable.has(change.id)) continue;
      if (!isInsideStayingTable(change)) continue;
      // A pPr change has no inline mark, so it must NOT go through the mark-based
      // contained-child planner (that would deref a null mark). But accepting a
      // table accepts its contents, so a contained pPr revision IS resolved here
      // — by id via planPprDecision — rather than left pending. This covers
      // accept-table-BY-ID (the pPr is not in the selection, only the sweep sees
      // it); accept-all resolves it in the main loop.
      if (change.pprChange) {
        cascadedInsideStayingTable.add(change.id);
        touched.add(change.id);
        const pprResult = planPprDecision({ ops, change, decision, retired });
        if (!pprResult.ok) return { ok: false, failure: pprResult.failure };
        continue;
      }
      const failureResult = planContainedInlineChild(change);
      if (failureResult) return { ok: false, failure: failureResult };
    }
  }

  if (!ops.length) {
    return {
      ok: false,
      failure: failure('NO_OP', 'decision target produced no operations.', {
        details: { selections: selections.map((s) => s.change.id) },
      }),
    };
  }

  // Identify child changes wholly inside removed ranges and mark them as
  // retired side effects. Per phase0-004 "Parent/Child Decision Rules":
  // accepting/rejecting a parent insertion retires children inside removed
  // content; accepting a parent deletion retires children wholly inside the
  // removed content; rejecting a parent deletion removes child insertions
  // that were meaningful only inside it.
  /** @type {Array<{ changeId: string }>} */
  const affectedChildren = [];
  // Inline changes suppressed because they were wholly inside a removed table
  // are already retired/touched above; surface them as affected side effects so
  // the bubble lifecycle resolves their threads.
  for (const id of suppressedInsideTable) {
    affectedChildren.push({ changeId: id });
  }
  // Inline children cascaded as part of a STAYING whole-table decision: surface
  // them so the bubble lifecycle resolves their threads alongside the parent.
  for (const id of cascadedInsideStayingTable) {
    affectedChildren.push({ changeId: id });
  }
  const seenChange = new Set();
  for (const change of graph.changes.values()) {
    // `changes` may hold a logical change under both an internal key and a
    // public alias; skip the duplicate visit.
    if (seenChange.has(change)) continue;
    seenChange.add(change);
    if (touched.has(change.id)) continue;
    const insideRemoved = change.segments.length
      ? change.segments.every((seg) => removedRanges.some((r) => r.from <= seg.from && r.to >= seg.to))
      : false;
    // An inline change wholly inside a removed table (even with no parent
    // relationship) is gone with the table — retire it as a side effect.
    if (insideRemoved && isInsideRemovedTable(change)) {
      retired.add(change.id);
      touched.add(change.id);
      affectedChildren.push({ changeId: change.id });
      continue;
    }
    if (!change.parent) continue;
    if (!retired.has(change.parent) && !touched.has(change.parent)) continue;
    if (insideRemoved) {
      retired.add(change.id);
      touched.add(change.id);
      affectedChildren.push({ changeId: change.id });
    }
  }

  const commentEffects = planCommentEffects({ doc: state.doc, removedRanges, resolvedRanges });

  // Convert comment node deletions into removeContent ops so apply respects
  // the same reverse-order pass. Removing the anchor nodes from inside
  // already-removed coverage is harmless (PM clips); explicitly listing them
  // makes shrink+keep cases idempotent.
  for (const del of commentEffects.nodeDeletes) {
    ops.push({ kind: 'removeContent', from: del.from, to: del.to });
  }

  /** @type {MutationPlan} */
  const plan = {
    ops,
    commentEffects: { ...commentEffects, _affectedChildren: affectedChildren },
    touchedChangeIds: touched,
    retiredChangeIds: retired,
    diagnostics,
  };
  return { ok: true, plan };
};

const planInsertionDecision = ({ ops, change, selection, decision, removedRanges, retired }) => {
  const isFull = selection.coverage === 'full';
  if (decision === 'accept') {
    // Accept insertion: keep content, remove the trackInsert mark.
    const ranges = isFull ? change.insertedSegments.map((s) => ({ from: s.from, to: s.to })) : selection.ranges;
    for (const range of ranges) {
      pushRemoveMarkOpsForRange({
        ops,
        segments: change.insertedSegments,
        range,
        changeId: change.id,
        side: SegmentSide.Inserted,
      });
    }
    if (isFull) retired.add(change.id);
    return;
  }
  // Reject insertion: remove inserted content.
  const ranges = isFull ? change.insertedSegments.map((s) => ({ from: s.from, to: s.to })) : selection.ranges;
  for (const range of ranges) {
    ops.push({
      kind: 'removeContent',
      from: range.from,
      to: range.to,
      changeId: change.id,
      side: SegmentSide.Inserted,
    });
    removedRanges.push({ from: range.from, to: range.to, cause: `reject-insertion:${change.id}` });
  }
  if (isFull) retired.add(change.id);
};

const planDeletionDecision = ({ ops, change, selection, decision, removedRanges, retired }) => {
  const isFull = selection.coverage === 'full';
  if (decision === 'accept') {
    // Accept deletion: remove tracked-deleted content permanently.
    const ranges = isFull ? change.deletedSegments.map((s) => ({ from: s.from, to: s.to })) : selection.ranges;
    for (const range of ranges) {
      ops.push({
        kind: 'removeContent',
        from: range.from,
        to: range.to,
        changeId: change.id,
        side: SegmentSide.Deleted,
      });
      removedRanges.push({ from: range.from, to: range.to, cause: `accept-deletion:${change.id}` });
    }
    // A whole-block deletion also deleted the paragraph MARK. Only a
    // full accept may collapse the paragraph — a partial accept leaves live
    // text behind, and the mark stays deleted until the rest is decided.
    if (isFull) {
      pushParagraphMarkOps({ ops, change, decision: 'accept', ranges });
      retired.add(change.id);
    }
    return;
  }
  // Reject deletion: remove the trackDelete mark; content stays as live.
  const ranges = isFull ? change.deletedSegments.map((s) => ({ from: s.from, to: s.to })) : selection.ranges;
  for (const range of ranges) {
    pushRemoveMarkOpsForRange({
      ops,
      segments: change.deletedSegments,
      range,
      changeId: change.id,
      side: SegmentSide.Deleted,
    });
  }
  if (isFull) {
    pushParagraphMarkOps({ ops, change, decision: 'reject', ranges });
    retired.add(change.id);
  }
};

/**
 * Queue the paragraph-mark half of a whole-block tracked deletion.
 *
 * The mark lives on paragraph node attrs (`markTrackChange`) rather than as an
 * inline mark, so it is not part of `deletedSegments`. We cannot resolve which
 * paragraph carries it from positions alone here — `applyPlan` does that against
 * the live doc, matching on the change id, and no-ops when the change was an
 * ordinary inline deletion that never touched a paragraph mark.
 *
 *   accept → content removed, then the emptied paragraph collapses into the
 *            next one (Word semantics: the successor keeps its own pPr, which
 *            is why the surviving items renumber and nothing empty is left).
 *   reject → clear the attr; the paragraph and its numbering stay put.
 */
const pushParagraphMarkOps = ({ ops, change, decision, ranges }) => {
  const anchor = ranges[0];
  if (!anchor) return;
  ops.push({
    kind: decision === 'accept' ? 'collapseParagraphMark' : 'clearParagraphMark',
    from: anchor.from,
    to: anchor.to,
    changeId: change.id,
    side: SegmentSide.Deleted,
  });
};

/**
 * Plan a whole-object structural decision (whole-table insert/delete).
 *
 * Semantics (spec §8 / §14):
 *   - accept insertion  → clear the rows' trackChange attrs (table becomes normal content).
 *   - reject insertion  → remove the whole table node.
 *   - accept deletion   → remove the whole table node.
 *   - reject deletion   → clear the rows' trackChange attrs (table restored).
 */
const planStructuralDecision = ({ ops, change, decision, removedRanges, retired }) => {
  const structural = change.structural;
  if (!structural) {
    return {
      ok: false,
      failure: failure('PRECONDITION_FAILED', `structural change "${change.id}" is missing its structural payload.`),
    };
  }

  // Fail closed on any structural shape that is NOT a whole-table insert/delete
  // (spec TC-OPS-003). A partial row subset or mixed sides within one
  // table is NOT a decidable whole-table change; row-level structural is out of
  // scope. We must NEVER route such a shape through the table-removal path. The
  // engine returns CAPABILITY_UNAVAILABLE and the document stays unmutated.
  if (structural.decidable === false || !structural.wholeTable) {
    return {
      ok: false,
      failure: failure(
        'CAPABILITY_UNAVAILABLE',
        'structural row-level revisions (partial rows or mixed sides) are not decidable; only whole-table insert/delete is supported.',
        { details: { changeId: change.id, reason: structural.undecidableReason ?? 'not-whole-table' } },
      ),
    };
  }

  const removeWholeTable =
    (structural.side === 'insertion' && decision === 'reject') ||
    (structural.side === 'deletion' && decision === 'accept');

  if (removeWholeTable) {
    ops.push({
      kind: 'removeContent',
      from: structural.tableFrom,
      to: structural.tableTo,
      changeId: change.id,
      side: structural.side === 'insertion' ? SegmentSide.Inserted : SegmentSide.Deleted,
    });
    removedRanges.push({
      from: structural.tableFrom,
      to: structural.tableTo,
      cause: `${decision}-structural:${change.id}`,
    });
    retired.add(change.id);
    return { ok: true };
  }

  // Otherwise the table stays and the revision is cleared from every grouped row.
  for (const row of structural.rows) {
    ops.push({
      kind: 'clearRowTrackChange',
      from: row.from,
      to: row.to,
      changeId: change.id,
    });
  }
  retired.add(change.id);
  return { ok: true };
};

/**
 * Plan an accept/reject for a paragraph-property change (w:pPrChange: tracked
 * numbering / alignment). There is no mark — the record lives on
 * `node.attrs.paragraphProperties.change`. Both decisions keep the paragraph:
 *   accept → drop the `change` record, keep the new properties (numbering stays)
 *   reject → restore the former properties recorded on the change (numbering gone)
 * Applied via `setNodeMarkup` in the mark pass (position-stable, same node size).
 *
 * @param {{ ops: any[], change: any, decision: 'accept'|'reject', retired: Set<string> }} input
 */
const planPprDecision = ({ ops, change, decision, retired }) => {
  const ppr = change.pprChange;
  if (!ppr) {
    return {
      ok: false,
      failure: failure('CAPABILITY_UNAVAILABLE', `change "${change.id}" is not a paragraph-property change.`),
    };
  }
  ops.push({
    kind: 'resolvePprChange',
    from: ppr.from,
    changeId: change.id,
    decision,
    formerProperties: ppr.formerProperties,
  });
  retired.add(change.id);
  return { ok: true };
};

/**
 * Resolve a standalone paragraph-mark deletion — the whole-block deletion of a
 * block that had no runs to strike. There is no content to remove, so accept
 * only has to collapse the emptied block into its successor and reject only
 * has to clear the record.
 */
const planParagraphMarkDecision = ({ ops, change, decision, retired }) => {
  const mark = change.paragraphMarkChange;
  if (!mark) {
    return {
      ok: false,
      failure: failure('CAPABILITY_UNAVAILABLE', `change "${change.id}" is not a paragraph-mark deletion.`),
    };
  }
  ops.push({
    kind: decision === 'accept' ? 'collapseParagraphMark' : 'clearParagraphMark',
    from: mark.from,
    to: mark.to,
    changeId: change.id,
    side: SegmentSide.Deleted,
  });
  retired.add(change.id);
  return { ok: true };
};

const planReplacementDecision = ({ ops, graph, change, decision, removedRanges, retired }) => {
  const inserted = change.insertedSegments;
  const deleted = change.deletedSegments;
  if (!inserted.length || !deleted.length) {
    return {
      ok: false,
      failure: failure('PRECONDITION_FAILED', `replacement "${change.id}" missing inserted or deleted side.`),
    };
  }
  if (decision === 'accept') {
    for (const seg of deleted) {
      ops.push({ kind: 'removeContent', from: seg.from, to: seg.to, changeId: change.id, side: SegmentSide.Deleted });
      removedRanges.push({ from: seg.from, to: seg.to, cause: `accept-replacement-deleted:${change.id}` });
    }
    for (const seg of inserted) {
      pushRemoveMarkOpsForSegment({
        ops,
        segment: seg,
        changeId: change.id,
        side: SegmentSide.Inserted,
      });
    }
  } else {
    // Reject replacement: remove inserted side, restore deleted side as live.
    for (const seg of inserted) {
      ops.push({ kind: 'removeContent', from: seg.from, to: seg.to, changeId: change.id, side: SegmentSide.Inserted });
      removedRanges.push({ from: seg.from, to: seg.to, cause: `reject-replacement-inserted:${change.id}` });
    }
    const parentRestore = getParentRestoreContext({ graph, change });
    for (const seg of deleted) {
      pushRemoveMarkOpsForSegment({
        ops,
        segment: seg,
        changeId: change.id,
        side: SegmentSide.Deleted,
      });
      if (parentRestore?.mark) {
        pushAddMarkOpsForSegment({
          ops,
          segment: seg,
          changeId: parentRestore.mark.attrs?.id || change.parent || change.id,
          side: parentRestore.mark.type.name === TrackInsertMarkName ? SegmentSide.Inserted : SegmentSide.Deleted,
          mark: parentRestore.mark,
        });
      }
    }
    for (const sibling of parentRestore?.siblingSegments ?? []) {
      pushRemoveMarkOpsForSegment({
        ops,
        segment: sibling,
        changeId: sibling.changeId,
        side: sibling.side,
      });
      pushAddMarkOpsForSegment({
        ops,
        segment: sibling,
        changeId: parentRestore.mark.attrs?.id || sibling.changeId,
        side: parentRestore.mark.type.name === TrackInsertMarkName ? SegmentSide.Inserted : SegmentSide.Deleted,
        mark: parentRestore.mark,
      });
      retired.add(sibling.changeId);
    }
  }
  retired.add(change.id);
  return { ok: true };
};

/**
 * Resolve ONE side of a replacement, treating each half as its own pending
 * change. The other half is left untouched (it survives as a standalone
 * insertion or deletion after the graph rebuilds), so the change id is NOT
 * retired here.
 *
 *   - deleted side, accept → remove the deleted content (deletion takes effect)
 *   - deleted side, reject → keep the text, drop the delete mark (deletion undone)
 *   - inserted side, accept → keep the text, drop the insert mark (insertion kept)
 *   - inserted side, reject → remove the inserted content (insertion undone)
 *
 * @param {{ ops: any[], change: any, decision: 'accept'|'reject', side: 'inserted'|'deleted', removedRanges: any[], retired: Set<string>, resolvedRanges: any[] }} input
 */
const planReplacementSideDecision = ({ ops, change, decision, side, removedRanges, resolvedRanges }) => {
  const inserted = change.insertedSegments;
  const deleted = change.deletedSegments;
  if (!inserted.length || !deleted.length) {
    return {
      ok: false,
      failure: failure('PRECONDITION_FAILED', `replacement "${change.id}" missing inserted or deleted side.`),
    };
  }

  const segments = side === SegmentSide.Inserted ? inserted : deleted;
  for (const seg of segments) {
    resolvedRanges.push({ from: seg.from, to: seg.to, cause: `${decision}-replacement-${side}:${change.id}` });
  }

  const removeContent = (seg, cause) => {
    ops.push({ kind: 'removeContent', from: seg.from, to: seg.to, changeId: change.id, side });
    removedRanges.push({ from: seg.from, to: seg.to, cause: `${cause}:${change.id}` });
  };
  const dropMark = (seg) => pushRemoveMarkOpsForSegment({ ops, segment: seg, changeId: change.id, side });

  if (side === SegmentSide.Deleted) {
    if (decision === 'accept') {
      for (const seg of deleted) removeContent(seg, 'accept-replacement-deleted-side');
    } else {
      for (const seg of deleted) dropMark(seg);
    }
  } else {
    if (decision === 'accept') {
      for (const seg of inserted) dropMark(seg);
    } else {
      for (const seg of inserted) removeContent(seg, 'reject-replacement-inserted-side');
    }
  }

  // Intentionally do NOT retire change.id: the untouched side remains pending
  // and re-projects as a standalone change on the next rebuild.
  return { ok: true };
};

const getParentRestoreContext = ({ graph, change }) => {
  const explicit = getExplicitParentRestoreContext({ graph, change });
  if (explicit) return explicit;
  return inferAdjacentParentRestoreContext({ graph, change });
};

const getExplicitParentRestoreContext = ({ graph, change }) => {
  if (!change.parent) return null;
  const parent = graph.changes.get(change.parent);
  if (!parent) return null;
  if (parent.type === CanonicalChangeType.Insertion) {
    const mark = parent.insertedSegments[0]?.mark ?? null;
    return mark ? { mark, siblingSegments: [] } : null;
  }
  if (parent.type === CanonicalChangeType.Deletion) {
    const mark = parent.deletedSegments[0]?.mark ?? null;
    return mark ? { mark, siblingSegments: [] } : null;
  }
  return null;
};

const inferAdjacentParentRestoreContext = ({ graph, change }) => {
  if (!change.deletedSegments.length || !change.segments.length) return null;
  const from = Math.min(...change.segments.map((segment) => segment.from));
  const to = Math.max(...change.segments.map((segment) => segment.to));
  const before = nearestSegmentBefore({ graph, change, from });
  const after = nearestSegmentAfter({ graph, change, to });
  if (!before || !after) return null;
  if (!areParentRestorePeers(before, after)) return null;

  return {
    mark: before.mark,
    siblingSegments: after.changeId === before.changeId ? [] : [after],
  };
};

const nearestSegmentBefore = ({ graph, change, from }) => {
  return graph.segments
    .filter((segment) => segment.changeId !== change.id && segment.to <= from)
    .sort((a, b) => b.to - a.to || b.from - a.from)[0];
};

const nearestSegmentAfter = ({ graph, change, to }) => {
  return graph.segments
    .filter((segment) => segment.changeId !== change.id && segment.from >= to)
    .sort((a, b) => a.from - b.from || a.to - b.to)[0];
};

const areParentRestorePeers = (left, right) => {
  if (!left || !right) return false;
  if (left.side !== right.side) return false;
  if (left.side !== SegmentSide.Inserted && left.side !== SegmentSide.Deleted) return false;
  if (left.markType !== right.markType) return false;
  return (
    left.attrs.author === right.attrs.author &&
    left.attrs.authorEmail === right.attrs.authorEmail &&
    left.attrs.date === right.attrs.date
  );
};

const planFormattingDecision = ({ ops, change, decision, retired }) => {
  for (const seg of change.formattingSegments) {
    if (decision === 'accept') {
      pushRemoveMarkOpsForSegment({
        ops,
        segment: seg,
        changeId: change.id,
        side: SegmentSide.Formatting,
      });
      continue;
    }

    for (const run of getSegmentMarkRuns(seg)) {
      const paragraphSplit = snapshotAttrsForType(run.mark.attrs?.before, 'paragraphSplit');
      if (paragraphSplit) {
        ops.push({
          kind: 'rejectParagraphSplit',
          from: run.from,
          to: run.to,
          changeId: change.id,
          side: SegmentSide.Formatting,
          mark: run.mark,
          anchor: paragraphSplit.anchor === 'source' ? 'source' : 'inserted',
        });
      } else {
        ops.push({
          kind: 'restoreFormat',
          from: run.from,
          to: run.to,
          changeId: change.id,
          side: SegmentSide.Formatting,
          mark: run.mark,
          beforeMarks: run.mark.attrs?.before ?? [],
          afterMarks: run.mark.attrs?.after ?? [],
        });
      }
    }
  }
  retired.add(change.id);
};

const snapshotAttrsForType = (snapshots, type) => {
  if (!Array.isArray(snapshots)) return null;
  const snapshot = snapshots.find((entry) => entry?.type === type);
  return snapshot?.attrs && typeof snapshot.attrs === 'object' ? snapshot.attrs : null;
};

const planPartialTextDecision = ({ ops, change, selection, decision, removedRanges, retired }) => {
  const side = change.type === CanonicalChangeType.Insertion ? SegmentSide.Inserted : SegmentSide.Deleted;
  const segments = side === SegmentSide.Inserted ? change.insertedSegments : change.deletedSegments;
  if (!segments.length) {
    return { ok: false, failure: failure('PRECONDITION_FAILED', `change "${change.id}" has no ${side} segments.`) };
  }

  const selectedRanges = mergeRanges(selection.ranges);
  const successorRanges = [];
  let logicalOffset = 0;
  let successorOrdinal = 0;

  for (const segment of segments) {
    pushRemoveMarkOpsForSegment({
      ops,
      segment,
      changeId: change.id,
      side,
    });

    const pieces = subtractRanges({ from: segment.from, to: segment.to }, selectedRanges);
    for (const piece of pieces) {
      const offsetStart = logicalOffset + (piece.from - segment.from);
      const offsetEnd = logicalOffset + (piece.to - segment.from);
      const successorId = deterministicSuccessorId({
        sourceId: change.id,
        revisionGroupId: segment.attrs?.revisionGroupId || change.revisionGroupId || change.id,
        side,
        offsetStart,
        offsetEnd,
        decision,
        ordinal: successorOrdinal,
      });
      successorOrdinal += 1;
      const successorMark = segment.mark.type.create({
        ...segment.mark.attrs,
        id: successorId,
        splitFromId: change.id,
        revisionGroupId: segment.attrs?.revisionGroupId || change.revisionGroupId || change.id,
      });
      ops.push({ kind: 'addMark', from: piece.from, to: piece.to, changeId: successorId, side, mark: successorMark });
      successorRanges.push({ id: successorId, from: piece.from, to: piece.to });
    }
    logicalOffset += segment.to - segment.from;
  }

  for (const range of selectedRanges) {
    if (change.type === CanonicalChangeType.Insertion && decision === 'reject') {
      ops.push({ kind: 'removeContent', from: range.from, to: range.to, changeId: change.id, side });
      removedRanges.push({ from: range.from, to: range.to, cause: `partial-reject-insertion:${change.id}` });
    } else if (change.type === CanonicalChangeType.Deletion && decision === 'accept') {
      ops.push({ kind: 'removeContent', from: range.from, to: range.to, changeId: change.id, side });
      removedRanges.push({ from: range.from, to: range.to, cause: `partial-accept-deletion:${change.id}` });
    }
  }

  retired.add(change.id);
  return { ok: true, createdChangeIds: successorRanges.map((entry) => entry.id) };
};

const pushRemoveMarkOpsForRange = ({ ops, segments, range, changeId, side }) => {
  for (const segment of segments) {
    if (segment.to <= range.from || segment.from >= range.to) continue;
    pushRemoveMarkOpsForSegment({
      ops,
      segment,
      changeId,
      side,
      from: Math.max(segment.from, range.from),
      to: Math.min(segment.to, range.to),
    });
  }
};

const pushRemoveMarkOpsForSegment = ({ ops, segment, changeId, side, from = segment.from, to = segment.to }) => {
  for (const run of getSegmentMarkRuns(segment)) {
    const clippedFrom = Math.max(from, run.from);
    const clippedTo = Math.min(to, run.to);
    if (clippedFrom >= clippedTo) continue;
    ops.push({
      kind: 'removeMark',
      from: clippedFrom,
      to: clippedTo,
      changeId,
      side,
      mark: run.mark,
    });
  }
};

const pushAddMarkOpsForSegment = ({ ops, segment, changeId, side, mark, from = segment.from, to = segment.to }) => {
  for (const run of getSegmentMarkRuns(segment)) {
    const clippedFrom = Math.max(from, run.from);
    const clippedTo = Math.min(to, run.to);
    if (clippedFrom >= clippedTo) continue;
    ops.push({
      kind: 'addMark',
      from: clippedFrom,
      to: clippedTo,
      changeId,
      side,
      mark,
    });
  }
};

const getSegmentMarkRuns = (segment) => {
  return segment.markRuns?.length ? segment.markRuns : [{ from: segment.from, to: segment.to, mark: segment.mark }];
};

const mergeRanges = (ranges) => {
  const sorted = ranges
    .filter((range) => range.from < range.to)
    .map((range) => ({ from: range.from, to: range.to }))
    .sort((a, b) => a.from - b.from || a.to - b.to);
  const merged = [];
  for (const range of sorted) {
    const last = merged[merged.length - 1];
    if (last && range.from <= last.to) {
      last.to = Math.max(last.to, range.to);
    } else {
      merged.push(range);
    }
  }
  return merged;
};

const subtractRanges = (range, removals) => {
  let pieces = [range];
  for (const removal of removals) {
    const next = [];
    for (const piece of pieces) {
      if (removal.to <= piece.from || removal.from >= piece.to) {
        next.push(piece);
        continue;
      }
      if (removal.from > piece.from) next.push({ from: piece.from, to: Math.min(removal.from, piece.to) });
      if (removal.to < piece.to) next.push({ from: Math.max(removal.to, piece.from), to: piece.to });
    }
    pieces = next;
  }
  return pieces.filter((piece) => piece.from < piece.to);
};

const deterministicSuccessorId = ({ sourceId, revisionGroupId, side, offsetStart, offsetEnd, decision, ordinal }) => {
  const input = `${sourceId}|${revisionGroupId}|${side}|${offsetStart}|${offsetEnd}|${decision}|${ordinal}`;
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `${sourceId}~${side}~${(hash >>> 0).toString(36)}`;
};

const findTextblockAt = (doc, pos) => {
  const bounded = Math.max(0, Math.min(pos, doc.content.size));
  const $pos = doc.resolve(bounded);
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth);
    if (node.isTextblock) {
      return { pos: $pos.before(depth), node };
    }
  }
  return null;
};

/**
 * Locate the paragraph carrying a `markTrackChange` for `changeId` at or around
 * `pos`. Checks the textblock containing `pos` first; when an accepted
 * deletion emptied the paragraph, `pos` can land on its boundary, so the
 * immediate neighbours are checked too. Matching on the change id keeps this
 * from touching an unrelated paragraph that happens to sit nearby.
 *
 * @returns {{ pos: number, node: import('prosemirror-model').Node } | null}
 */
const findParagraphMarkChangeAt = (tr, pos, changeId) => {
  const carries = (node) => node?.attrs?.markTrackChange?.id === changeId;
  const clamped = Math.max(0, Math.min(pos, tr.doc.content.size));
  const block = findTextblockAt(tr.doc, clamped);
  if (block && carries(block.node)) return block;

  let found = null;
  tr.doc.descendants((node, nodePos) => {
    if (found) return false;
    if (node.isTextblock && carries(node)) {
      found = { pos: nodePos, node };
      return false;
    }
    return undefined;
  });
  return found;
};

const rejectParagraphSplitAt = (tr, from, anchor = 'inserted') => {
  const block = findTextblockAt(tr.doc, from);
  if (!block) return false;
  const joinPos = anchor === 'source' ? block.pos + block.node.nodeSize : block.pos;
  if (joinPos <= 0 || joinPos >= tr.doc.content.size || !canJoin(tr.doc, joinPos)) return false;
  tr.join(joinPos);
  return true;
};

// ---------------------------------------------------------------------------
// Plan application
// ---------------------------------------------------------------------------

const applyPlan = ({ state, plan }) => {
  const tr = state.tr;
  tr.setMeta('inputType', 'acceptReject');

  // Mark mutations are position-stable and must run before content deletion:
  // partial decisions remove the source mark, add successor marks to surviving
  // text, then delete only the selected text when the decision semantics ask
  // for removal. Applying all ops in one reversed pass can make a broad source
  // mark removal erase freshly-added successor marks.
  const sortedOps = [...plan.ops].sort((a, b) => a.from - b.from || a.to - b.to);
  const markOps = sortedOps.filter((op) => op.kind !== 'removeContent');
  const contentOps = sortedOps.filter((op) => op.kind === 'removeContent').reverse();
  // Structural paragraph joins are NOT position-stable mark operations: tr.join()
  // changes document structure and shifts every later position. Doing the join
  // inside the mark pass invalidates the source positions of later mark ops, which
  // breaks decisions that reject multiple paragraph splits in one transaction.
  // We therefore remove the track-format mark in the position-stable mark pass and
  // defer the join to a mapped structural phase below.
  const splitJoinOps = sortedOps.filter((op) => op.kind === 'rejectParagraphSplit').reverse();
  // Collapsing an accepted paragraph-mark deletion is a join too, so it
  // shares the deferred structural phase for the same reason.
  const paragraphMarkOps = sortedOps.filter((op) => op.kind === 'collapseParagraphMark').reverse();

  try {
    for (const op of markOps) {
      if (op.kind === 'removeMark' && op.mark) {
        tr.step(new RemoveMarkStep(op.from, op.to, op.mark));
        continue;
      }
      if (op.kind === 'addMark' && op.mark) {
        tr.step(new AddMarkStep(op.from, op.to, op.mark));
        continue;
      }
      if (op.kind === 'restoreFormat' && op.mark) {
        // Remove the "after" marks first so the restored "before" marks aren't
        // shadowed by overlap matching (mirrors legacy rejectTrackedChangesBetween).
        for (const afterSnapshot of op.afterMarks ?? []) {
          const liveMark = findMarkInRangeBySnapshot({
            doc: tr.doc,
            from: op.from,
            to: op.to,
            snapshot: afterSnapshot,
          });
          if (liveMark) {
            tr.step(new RemoveMarkStep(op.from, op.to, liveMark));
          }
        }
        for (const beforeSnapshot of op.beforeMarks ?? []) {
          const markType = state.schema.marks[beforeSnapshot.type];
          if (!markType) continue;
          tr.step(new AddMarkStep(op.from, op.to, markType.create(beforeSnapshot.attrs)));
        }
        tr.step(new RemoveMarkStep(op.from, op.to, op.mark));
        continue;
      }
      if (op.kind === 'rejectParagraphSplit' && op.mark) {
        // Position-stable part only: drop the tracked-format mark here. The join
        // runs in the structural phase after content removal.
        tr.step(new RemoveMarkStep(op.from, op.to, op.mark));
        continue;
      }
      if (op.kind === 'clearRowTrackChange') {
        // Whole-table accept-insert / reject-delete: the table survives as
        // normal content. setNodeMarkup is position-stable (same node size), so
        // it is safe in the mark pass. Map through the accumulated transaction
        // mapping in case an earlier op shifted positions.
        const mappedFrom = tr.mapping.map(op.from, 1);
        const rowNode = tr.doc.nodeAt(mappedFrom);
        if (rowNode && rowNode.type.name === 'tableRow') {
          tr.setNodeMarkup(mappedFrom, undefined, { ...rowNode.attrs, trackChange: null });
        }
        continue;
      }
      if (op.kind === 'clearParagraphMark') {
        // Rejected whole-block deletion: the paragraph mark survives. Clearing
        // the attr is position-stable, so it belongs in the mark pass.
        const target = findParagraphMarkChangeAt(tr, tr.mapping.map(op.from, 1), op.changeId);
        if (target) {
          tr.setNodeMarkup(target.pos, undefined, { ...target.node.attrs, markTrackChange: null });
        }
        continue;
      }
      if (op.kind === 'resolvePprChange') {
        // Paragraph-property accept/reject. setNodeMarkup is position-stable, so
        // it is safe in the mark pass; map through the accumulated mapping in
        // case an earlier op shifted positions.
        const mappedFrom = tr.mapping.map(op.from, 1);
        const node = tr.doc.nodeAt(mappedFrom);
        const pp = node?.attrs?.paragraphProperties;
        if (node && pp && pp.change) {
          if (op.decision === 'accept') {
            // Keep the new properties; drop only the change record. Top-level
            // numberingProperties / listRendering already reflect the accepted
            // state, so they are left untouched.
            const kept = { ...pp };
            delete kept.change;
            tr.setNodeMarkup(mappedFrom, undefined, { ...node.attrs, paragraphProperties: kept });
          } else {
            // Reject: restore the former paragraph properties. The attach path
            // mirrors numbering onto the TOP-LEVEL numberingProperties (and the
            // numbering plugin computes listRendering), and the block index /
            // list rendering read those as a fallback — so sync them to the
            // restored state here too. Otherwise a rejected block keeps behaving
            // and rendering as a numbered list item in any path that does not
            // re-run the numbering plugin.
            const former = { ...(op.formerProperties || {}) };
            const nextAttrs = {
              ...node.attrs,
              paragraphProperties: former,
              numberingProperties: former.numberingProperties ?? null,
            };
            if (!former.numberingProperties) nextAttrs.listRendering = null;
            tr.setNodeMarkup(mappedFrom, undefined, nextAttrs);
          }
        }
        continue;
      }
    }
    for (const op of contentOps) {
      tr.step(new ReplaceStep(op.from, op.to, Slice.empty));
    }
    // Structural phase: apply paragraph-split joins in reverse document order,
    // mapping each original source position through the accumulated transaction
    // mapping so prior content removals / earlier joins do not desync positions.
    // Fail closed if a join cannot be applied so the whole decision aborts.
    for (const op of splitJoinOps) {
      const mappedFrom = tr.mapping.map(op.from, 1);
      if (!rejectParagraphSplitAt(tr, mappedFrom, op.anchor)) {
        throw new Error(`could not join paragraph split for tracked change "${op.changeId ?? ''}".`);
      }
    }
    // Accepted paragraph-mark deletions. The content is already gone;
    // collapsing the emptied paragraph into its successor is what removes the
    // list item itself and lets the rest of the list renumber.
    for (const op of paragraphMarkOps) {
      const mappedFrom = tr.mapping.map(op.from, 1);
      const target = findParagraphMarkChangeAt(tr, mappedFrom, op.changeId);
      // No target means the change was an ordinary inline deletion with no
      // paragraph mark of its own — nothing structural to do.
      if (!target) continue;
      tr.setNodeMarkup(target.pos, undefined, { ...target.node.attrs, markTrackChange: null });
      const joinPos = target.pos + tr.doc.nodeAt(target.pos).nodeSize;
      if (joinPos <= 0 || joinPos >= tr.doc.content.size || !canJoin(tr.doc, joinPos)) {
        // Last block in its parent, or an unjoinable successor. The mark is
        // resolved and the content is gone; leaving the empty paragraph beats
        // failing the whole decision.
        continue;
      }
      tr.join(joinPos);
    }
  } catch (error) {
    return {
      ok: false,
      failure: failure(
        'PRECONDITION_FAILED',
        /** @type {Error} */ (error).message ?? 'failed to apply mutation plan.',
        {
          details: { error: String(error) },
        },
      ),
    };
  }

  // Tracked-change plugin meta — preserve compatibility with the comments
  // plugin which listens for tracked-change resolution to update bubbles.
  tr.setMeta(TrackChangesBasePluginKey, {
    insertedMark: null,
    deletionMark: null,
    deletionNodes: [],
    step: null,
    emitCommentEvent: true,
    decisionTouchedChangeIds: Array.from(plan.touchedChangeIds),
    decisionRetiredChangeIds: Array.from(plan.retiredChangeIds),
  });
  tr.setMeta(CommentsPluginKey, { type: 'force' });
  tr.setMeta('skipTrackChanges', true);

  return {
    ok: true,
    tr,
    touchedChangeIds: plan.touchedChangeIds,
    receipt: buildReceipt({ plan }),
  };
};

const buildReceipt = ({ plan }) => {
  /** @type {DecisionReceiptEntities} */
  const receipt = {
    createdChangeIds: collectCreatedChangeIds(plan),
    updatedChangeIds: [],
    removedChangeIds: Array.from(plan.retiredChangeIds).map((id) => ({ id, cause: 'decision' })),
    deletedComments: plan.commentEffects.entityDeletes,
    detachedComments: plan.commentEffects.entityDetaches,
    shrunkenComments: plan.commentEffects.entityShrinks.map(({ id, cause }) => ({ id, cause })),
    affectedChildren: plan.commentEffects._affectedChildren ?? [],
  };
  return receipt;
};

const collectCreatedChangeIds = (plan) => {
  const ids = new Set();
  for (const op of plan.ops) {
    if (op.kind === 'addMark' && op.changeId) ids.add(op.changeId);
  }
  return Array.from(ids);
};

// ---------------------------------------------------------------------------
// Bubble lifecycle support
// ---------------------------------------------------------------------------

/**
 * Build the bubble lifecycle payload from a successful decision result. The
 * caller (acceptTrackedChangesBetween wrapper) emits this through the editor
 * once the transaction dispatches so consumers update from decision data,
 * not from re-scanning marks after dispatch.
 *
 * @param {Object} input
 * @param {DecisionResult} input.result
 * @param {object} input.editor
 * @returns {Array<{ type: 'trackedChange', event: 'resolve'|'update', changeId: string, resolvedById?: string, resolvedByEmail?: string, resolvedByName?: string }>}
 */
export const buildDecisionBubbleEvents = ({ result, editor }) => {
  const resolvedById = editor?.options?.user?.id;
  const resolvedByEmail = editor?.options?.user?.email;
  const resolvedByName = editor?.options?.user?.name;
  /** @type {Array<{ type: 'trackedChange', event: 'resolve'|'update', changeId: string, resolvedById?: string, resolvedByEmail?: string, resolvedByName?: string }>} */
  const events = [];
  for (const entry of result.receipt.removedChangeIds) {
    events.push({
      type: 'trackedChange',
      event: 'resolve',
      changeId: entry.id,
      resolvedById,
      resolvedByEmail,
      resolvedByName,
    });
  }
  for (const child of result.receipt.affectedChildren) {
    events.push({
      type: 'trackedChange',
      event: 'resolve',
      changeId: child.changeId,
      resolvedById,
      resolvedByEmail,
      resolvedByName,
    });
  }
  return events;
};

export { TRACKED_MARK_NAMES };
